import { LOCALES, type Locale } from '@ambernord/shared';
import { cn } from '@ambernord/ui';
import { LogOut, Menu, Moon, Search, ShieldCheck, Sun } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { useTheme } from '../theme.js';

/**
 * Augšjosla: zīmols, ⌘K meklēšana, valoda, tēma, lietotāja izvēlne.
 * Lietojama arī savrupi (Login/Mfa/Register — bez `onOpenPalette`/`onToggleSidebar`).
 */
export function AppHeader({
  showSignOut = false,
  onToggleSidebar,
  onOpenPalette,
}: {
  showSignOut?: boolean;
  onToggleSidebar?: () => void;
  onOpenPalette?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Lietotāja izvēlne: aizver ar klikšķi ārpusē vai Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const initial = (user?.email?.[0] ?? '?').toUpperCase();
  const isMac =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform ?? '');

  const iconBtn =
    'inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white';

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur-md dark:border-slate-800 dark:bg-slate-950/80">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          {onToggleSidebar ? (
            <button
              type="button"
              onClick={onToggleSidebar}
              aria-label={t('sidebar.openMenu')}
              className={cn(iconBtn, 'lg:hidden')}
            >
              <Menu className="h-5 w-5" aria-hidden />
            </button>
          ) : null}
          <Link to="/" className="flex items-baseline gap-2">
            <span className="text-lg font-extrabold tracking-tight text-brand-700 dark:text-brand-400">
              AmberNord
            </span>
            <span className="hidden text-xs font-semibold uppercase tracking-wider text-slate-400 sm:inline">
              B2B Portal
            </span>
          </Link>
        </div>

        <div className="flex items-center gap-2">
          {onOpenPalette ? (
            <>
              <button
                type="button"
                onClick={onOpenPalette}
                className="hidden h-9 w-56 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-400 transition-colors hover:border-slate-400 hover:text-slate-500 md:flex dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600"
              >
                <Search className="h-4 w-4 shrink-0" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-left">{t('palette.search')}</span>
                <kbd className="shrink-0 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold dark:border-slate-700 dark:bg-slate-800">
                  {isMac ? '⌘K' : 'Ctrl K'}
                </kbd>
              </button>
              <button
                type="button"
                onClick={onOpenPalette}
                aria-label={t('palette.search')}
                className={cn(iconBtn, 'md:hidden')}
              >
                <Search className="h-4 w-4" aria-hidden />
              </button>
            </>
          ) : null}

          <label className="sr-only" htmlFor="lang">
            {t('language')}
          </label>
          <select
            id="lang"
            className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm font-medium dark:border-slate-700 dark:bg-slate-900"
            value={i18n.resolvedLanguage}
            onChange={(e) => void i18n.changeLanguage(e.target.value as Locale)}
          >
            {LOCALES.map((l) => (
              <option key={l} value={l}>
                {l.toUpperCase()}
              </option>
            ))}
          </select>

          <button type="button" onClick={toggle} aria-label={t('theme.toggle')} className={iconBtn}>
            {theme === 'dark' ? (
              <Sun className="h-4 w-4" aria-hidden />
            ) : (
              <Moon className="h-4 w-4" aria-hidden />
            )}
          </button>

          {showSignOut && user ? (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                aria-label={t('userMenu.openMenu')}
                aria-expanded={menuOpen}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-600 text-sm font-bold text-white ring-2 ring-transparent transition-shadow hover:ring-brand-300 dark:hover:ring-brand-700"
              >
                {initial}
              </button>
              <div
                className={cn(
                  'absolute right-0 top-11 z-50 w-64 origin-top-right rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl transition-all duration-150 dark:border-slate-800 dark:bg-slate-900',
                  menuOpen ? 'scale-100 opacity-100' : 'pointer-events-none scale-95 opacity-0',
                )}
              >
                <div className="border-b border-slate-100 px-3 py-2.5 dark:border-slate-800">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">
                    {t('userMenu.signedInAs')}
                  </p>
                  <p className="mt-0.5 truncate text-sm font-semibold text-slate-900 dark:text-white">
                    {user.email}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    {t('userMenu.role')}: <span className="font-medium">{user.role}</span>
                  </p>
                </div>
                <Link
                  to="/settings/security"
                  onClick={() => setMenuOpen(false)}
                  className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
                >
                  <ShieldCheck className="h-4 w-4" aria-hidden />
                  {t('userMenu.security')}
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    void logout();
                  }}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-red-50 hover:text-red-700 dark:text-slate-300 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                >
                  <LogOut className="h-4 w-4" aria-hidden />
                  {t('actions.signOut')}
                </button>
              </div>
            </div>
          ) : showSignOut ? (
            <button
              type="button"
              onClick={() => void logout()}
              className={cn(iconBtn, 'w-auto px-3 text-sm font-semibold')}
            >
              {t('actions.signOut')}
            </button>
          ) : null}
        </div>
      </div>
    </header>
  );
}
